// Un PDF dentro de la hoja de vista previa (04-11-2026): las páginas las pinta la oficina con pdf.js —no el lector del
// navegador en un iframe—, una debajo de otra, ajustadas al ancho (como mucho 1104 px), con un carril de miniaturas a la
// izquierda y la píldora «1 de 11» abajo.
//
// Memoria (100 páginas, el iPad): solo existen los lienzos de las páginas a la vista y de una pantalla arriba y abajo; las
// demás son un hueco de su tamaño. Cada lienzo va a la densidad de la pantalla con tope 2× y como mucho 16 M de píxeles
// (el límite de un lienzo en Safari). Al cambiar el tamaño se estira el dibujo que hay y se vuelve a pintar a los 150 ms.
// Mientras pdf.js carga se ve la portada que ya dibujó el servidor: nunca un blanco.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Cargando, NoSePudo, Pildora, useDespertar, useGestosZoom, type ControlCuerpo, type PropsCuerpo } from './piezas';
import { abrirPdf, type DocumentoPdf } from './pdf';
import { multiplicar, PX_POR_PUNTO } from './zoom';

// max-w-6xl (1152 px) menos el margen de 24 px a cada lado: el ancho máximo de una página «ajustada».
const ANCHO_MAX = 1104;
const MARGEN = 24;
const MAX_PIXELES = 16_000_000;
const ANCHO_MINIATURA = 128;

interface Tamano {
  w: number;
  h: number;
}

type Carga = { estado: 'cargando' } | { estado: 'error' } | { estado: 'listo'; doc: DocumentoPdf; tamanos: Tamano[] };

function usePdf(url: string): Carga {
  const [c, setC] = useState<Carga>({ estado: 'cargando' });
  useEffect(() => {
    let vivo = true;
    let destruir: (() => void) | null = null;
    setC({ estado: 'cargando' });
    (async () => {
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error(String(r.status));
      const abierto = await abrirPdf(await r.arrayBuffer());
      destruir = abierto.destruir;
      if (!vivo) return abierto.destruir();
      // Los tamaños de todas las páginas (barato: no pinta nada) para reservar su hueco desde el principio.
      const tamanos = await Promise.all(
        Array.from({ length: abierto.doc.numPages }, (_, i) =>
          abierto.doc.getPage(i + 1).then((p) => {
            const v = p.getViewport({ scale: 1 });
            return { w: v.width, h: v.height };
          }),
        ),
      );
      if (vivo) setC({ estado: 'listo', doc: abierto.doc, tamanos });
    })().catch(() => vivo && setC({ estado: 'error' }));
    return () => {
      vivo = false;
      destruir?.();
    };
  }, [url]);
  return c;
}

/** Un valor que solo cambia cuando lleva `ms` quieto (el repintado tras un cambio de tamaño). */
function useQuieto<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const reloj = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(reloj);
  }, [valor, ms]);
  return v;
}

/** Cancelar un dibujo (la página salió de la vista, cambió el tamaño) es lo normal: pdf.js lo dice con esta excepción. */
const esCancelacion = (err: unknown): boolean => typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'RenderingCancelledException';

/**
 * Pinta la página `n` en el lienzo, a `anchoCss` px de ancho y la densidad de la pantalla. Devuelve si NO se pudo pintar.
 * 05-11 (auditoría de código): al dejar de pintarla (sale de la vista, se cierra) se libera lo que pdf.js guardaba de esa
 * página (`cleanup`): con 100 páginas recorridas, la memoria crecía sin parar en el iPad. Y solo la cancelación se calla:
 * una página rota lo dice en su hueco en vez de quedarse en blanco como si cargara.
 */
function usePintar(doc: DocumentoPdf, n: number, anchoCss: number, activo: boolean, lienzo: React.RefObject<HTMLCanvasElement | null>): boolean {
  const [fallo, setFallo] = useState(false);
  useEffect(() => {
    if (!activo || anchoCss <= 0) return;
    let vivo = true;
    let tarea: { cancel: () => void } | null = null;
    let pagina: { cleanup: () => unknown } | null = null;
    doc
      .getPage(n)
      .then((p) => {
        pagina = p;
        const c = lienzo.current;
        if (!vivo || !c) return;
        const base = p.getViewport({ scale: 1 });
        let escala = (anchoCss / base.width) * Math.min(window.devicePixelRatio || 1, 2);
        const pixeles = base.width * base.height * escala * escala;
        if (pixeles > MAX_PIXELES) escala *= Math.sqrt(MAX_PIXELES / pixeles);
        const vista = p.getViewport({ scale: escala });
        // Se pinta aparte y se copia al final: el dibujo anterior sigue a la vista hasta que el nuevo está listo.
        const aparte = document.createElement('canvas');
        aparte.width = Math.floor(vista.width);
        aparte.height = Math.floor(vista.height);
        const render = p.render({ canvas: aparte, viewport: vista });
        tarea = render;
        return render.promise.then(() => {
          if (!vivo) return;
          c.width = aparte.width;
          c.height = aparte.height;
          c.getContext('2d')?.drawImage(aparte, 0, 0);
          c.dataset.pintada = 'si';
          setFallo(false);
        });
      })
      .catch((err: unknown) => {
        if (!esCancelacion(err) && vivo) setFallo(true);
      });
    return () => {
      vivo = false;
      tarea?.cancel();
      pagina?.cleanup();
    };
  }, [doc, n, anchoCss, activo, lienzo]);
  return fallo;
}

function Pagina({ doc, n, ancho, alto, visible, refPagina }: { doc: DocumentoPdf; n: number; ancho: number; alto: number; visible: boolean; refPagina: (el: HTMLDivElement | null) => void }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const anchoQuieto = useQuieto(Math.round(ancho), 150);
  const fallo = usePintar(doc, n, anchoQuieto, visible, lienzo);
  return (
    <div
      ref={refPagina}
      data-pagina={n}
      aria-label={t('oficinaV2.vistaPrevia.pagina', { n })}
      role="img"
      style={{ width: ancho, height: alto }}
      className="relative shrink-0 overflow-hidden rounded-o-s bg-oficina-elevada shadow-o-flota"
    >
      {visible ? <canvas ref={lienzo} className="block h-full w-full" /> : null}
      {fallo ? (
        <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-o-sub text-oficina-texto3" data-testid="pagina-rota">
          {t('oficinaV2.vistaPrevia.paginaRota', { n })}
        </p>
      ) : null}
    </div>
  );
}

function Miniatura({ doc, n, tamano, activa, alPulsar, raiz }: { doc: DocumentoPdf; n: number; tamano: Tamano; activa: boolean; alPulsar: () => void; raiz: HTMLElement | null }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const [vista, setVista] = useState(false);
  useEffect(() => {
    const el = boton.current;
    if (!el || !raiz || vista || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setVista(true), { root: raiz, rootMargin: '200px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [raiz, vista]);
  usePintar(doc, n, ANCHO_MINIATURA, vista, lienzo);
  useEffect(() => {
    if (activa) boton.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activa]);
  return (
    <button ref={boton} type="button" onClick={alPulsar} aria-current={activa ? 'page' : undefined} aria-label={t('oficinaV2.vistaPrevia.pagina', { n })} className="group flex w-full flex-col items-center gap-1.5 outline-none">
      <span
        style={{ height: (ANCHO_MINIATURA * tamano.h) / tamano.w }}
        className={cn(
          'block w-32 overflow-hidden rounded-o-s bg-oficina-elevada transition-shadow duration-150 ease-o-apple group-focus-visible:ring-2 group-focus-visible:ring-oficina-texto2',
          activa ? 'ring-2 ring-oficina-texto' : 'ring-1 ring-oficina-linea group-hover:ring-oficina-pulsado',
        )}
      >
        {vista ? <canvas ref={lienzo} className="block h-full w-full" /> : null}
      </span>
      <span className={cn('text-o-nota tabular-nums', activa ? 'font-semibold text-oficina-texto' : 'text-oficina-texto3')}>{n}</span>
    </button>
  );
}

export const VisorPdf = forwardRef<ControlCuerpo, PropsCuerpo>(function VisorPdf({ elemento, zoom, alZoom, carril }, ref) {
  const carga = usePdf(elemento.url);
  const [zona, setZona] = useState<HTMLDivElement | null>(null);
  const [rail, setRail] = useState<HTMLElement | null>(null);
  const [anchoZona, setAnchoZona] = useState(0);
  const [actual, setActual] = useState(1);
  const [visibles, setVisibles] = useState<Set<number>>(() => new Set([1, 2]));
  const paginas = useRef(new Map<number, HTMLDivElement>());
  const tamanos = carga.estado === 'listo' ? carga.tamanos : null;
  const total = tamanos?.length ?? elemento.paginas ?? 0;

  // El ancho disponible (cambia al abrir o cerrar el carril, o al girar el iPad).
  useLayoutEffect(() => {
    if (!zona) return;
    const medir = () => setAnchoZona(zona.clientWidth);
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(zona);
    return () => ro.disconnect();
  }, [zona]);

  const primera = tamanos?.[0] ?? { w: 1, h: 1 / (elemento.proporcion ?? 0.75) };
  const anchoAjustado = Math.max(120, Math.min(ANCHO_MAX, anchoZona - 2 * MARGEN));
  // El % que es «Ajustar» para la página 1 (desde ahí siguen + y −).
  const ajuste = useCallback(() => (anchoAjustado / (primera.w * PX_POR_PUNTO)) * 100, [anchoAjustado, primera.w]);
  const anchoDe = useCallback((tam: Tamano) => (zoom === 'ajustar' ? anchoAjustado : tam.w * PX_POR_PUNTO * (zoom / 100)), [zoom, anchoAjustado]);

  const irA = useCallback(
    (n: number) => {
      const el = paginas.current.get(n);
      if (!zona || !el) return;
      zona.scrollTo({ top: el.offsetTop - MARGEN });
      setActual(n);
    },
    [zona],
  );

  useImperativeHandle(
    ref,
    () => ({
      anterior: () => irA(Math.max(1, actual - 1)),
      siguiente: () => irA(Math.min(total, actual + 1)),
      inicio: () => irA(1),
      fin: () => irA(total),
      ajuste,
      desplazable: () => zona,
    }),
    [irA, actual, total, ajuste, zona],
  );

  // Qué páginas están a la vista (± una pantalla): solo esas tienen lienzo.
  useEffect(() => {
    if (!zona || !tamanos || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entradas) =>
        setVisibles((antes) => {
          const sig = new Set(antes);
          for (const e of entradas) {
            const n = Number((e.target as HTMLElement).dataset.pagina);
            if (e.isIntersecting) sig.add(n);
            else sig.delete(n);
          }
          return sig;
        }),
      { root: zona, rootMargin: '100% 0px' },
    );
    paginas.current.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [zona, tamanos]);

  // La página actual: la que ocupa el 40 % de arriba de la vista.
  useEffect(() => {
    if (!zona) return;
    let pendiente = false;
    const mirar = () => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(() => {
        pendiente = false;
        const linea = zona.scrollTop + zona.clientHeight * 0.4;
        let n = 1;
        paginas.current.forEach((el, k) => {
          if (el.offsetTop <= linea && k > n) n = k;
        });
        setActual(n);
      });
    };
    zona.addEventListener('scroll', mirar, { passive: true });
    return () => zona.removeEventListener('scroll', mirar);
  }, [zona]);

  // Al cambiar el tamaño, la página que se estaba leyendo se queda en su sitio.
  const ancla = useRef<{ n: number; fraccion: number } | null>(null);
  const anchoPagina1 = Math.round(anchoDe(primera));
  useLayoutEffect(() => {
    const a = ancla.current;
    const el = a ? paginas.current.get(a.n) : null;
    if (zona && a && el) zona.scrollTop = el.offsetTop + a.fraccion * el.offsetHeight - zona.clientHeight * 0.4;
  }, [anchoPagina1, zona]);
  useEffect(() => {
    if (!zona) return;
    const guardar = () => {
      const el = paginas.current.get(actual);
      if (el) ancla.current = { n: actual, fraccion: (zona.scrollTop + zona.clientHeight * 0.4 - el.offsetTop) / Math.max(1, el.offsetHeight) };
    };
    guardar();
    zona.addEventListener('scroll', guardar, { passive: true });
    return () => zona.removeEventListener('scroll', guardar);
  }, [zona, actual]);

  useGestosZoom(zona, (f) => alZoom(multiplicar(zoom, ajuste(), f)));
  const despierta = useDespertar(zona);

  const anchoContenido = tamanos ? Math.max(...tamanos.map((tam) => anchoDe(tam))) + 2 * MARGEN : undefined;

  return (
    <div className="flex min-h-0 flex-1">
      {carril && carga.estado === 'listo' ? (
        <nav ref={setRail} aria-label={t('oficinaV2.vistaPrevia.carril')} data-testid="carril-paginas" className="w-44 shrink-0 space-y-4 overflow-y-auto border-r border-oficina-linea bg-oficina-fondo px-6 py-5">
          {carga.tamanos.map((tam, i) => (
            <Miniatura key={i} doc={carga.doc} n={i + 1} tamano={tam} activa={actual === i + 1} alPulsar={() => irA(i + 1)} raiz={rail} />
          ))}
        </nav>
      ) : null}
      <div className="relative min-w-0 flex-1 bg-oficina-fondo">
        <div ref={setZona} data-autofoco tabIndex={-1} data-testid="paginas-pdf" className="relative h-full w-full touch-pan-x touch-pan-y overflow-auto outline-none">
          {carga.estado === 'error' ? (
            <NoSePudo />
          ) : carga.estado === 'cargando' ? (
            // La portada del servidor en el sitio de la página 1 mientras llega pdf.js: nunca un blanco.
            elemento.portada ? (
              <div className="mx-auto px-6 py-6" style={{ width: anchoAjustado + 2 * MARGEN }}>
                <img
                  src={elemento.portada}
                  alt=""
                  style={{ width: anchoAjustado, aspectRatio: String(elemento.proporcion ?? 0.75) }}
                  className="block rounded-o-s bg-oficina-elevada object-cover object-top shadow-o-flota"
                />
              </div>
            ) : (
              <Cargando />
            )
          ) : (
            <div className="mx-auto flex flex-col items-center gap-6 py-6" style={{ width: anchoContenido }}>
              {carga.tamanos.map((tam, i) => {
                const ancho = anchoDe(tam);
                return (
                  <Pagina
                    key={i}
                    doc={carga.doc}
                    n={i + 1}
                    ancho={ancho}
                    alto={(ancho * tam.h) / tam.w}
                    visible={visibles.has(i + 1)}
                    refPagina={(el) => {
                      if (el) paginas.current.set(i + 1, el);
                      else paginas.current.delete(i + 1);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
        {total > 0 && carga.estado === 'listo' ? (
          <Pildora actual={actual} total={total} alAnterior={() => irA(Math.max(1, actual - 1))} alSiguiente={() => irA(Math.min(total, actual + 1))} despierta={despierta} />
        ) : null}
      </div>
    </div>
  );
});
